import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import * as mcpApi from '@/lib/mcp/api';
import type { UnifiedTool, CategoryCount, ProviderInfo, ToolSource } from '@/lib/mcp/types';
import {
  Search,
  Wrench,
  Zap,
  Database,
  Star,
  AlertTriangle,
  ChevronRight,
  RefreshCw,
  Plus,
} from 'lucide-react';
import { toast } from 'sonner';

export const Route = createFileRoute('/_dashboard/tools/')({
  component: ToolsPage,
});

const SOURCE_LABELS: Record<ToolSource, string> = {
  native_utility: 'Native',
  provider_endpoint: 'Provider',
  external_mcp: 'External',
};

const SOURCE_COLORS: Record<ToolSource, string> = {
  native_utility: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  provider_endpoint: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  external_mcp: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
};

function ToolsPage() {
  const navigate = useNavigate();
  const [tools, setTools] = useState<UnifiedTool[]>([]);
  const [categories, setCategories] = useState<CategoryCount[]>([]);
  const [providers, setProviders] = useState<ProviderInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSource, setSelectedSource] = useState<string>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedProvider, setSelectedProvider] = useState<string>('all');
  const [showPopularOnly, setShowPopularOnly] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 50,
    offset: 0,
    hasMore: false,
  });
  const [stats, setStats] = useState<{
    totalTools: number;
    nativeTools: number;
    providerTools: number;
  } | null>(null);

  const fetchTools = useCallback(async () => {
    setLoading(true);
    try {
      const response = await mcpApi.listTools({
        source: selectedSource !== 'all' ? (selectedSource as ToolSource) : undefined,
        category: selectedCategory !== 'all' ? selectedCategory : undefined,
        provider: selectedProvider !== 'all' ? selectedProvider : undefined,
        popular: showPopularOnly ? true : undefined,
        search: searchQuery || undefined,
        limit: pagination.limit,
        offset: pagination.offset,
      });

      setTools(response.data);
      setPagination(response.pagination);
    } catch (error) {
      toast.error((error as Error).message || 'Failed to fetch tools');
    } finally {
      setLoading(false);
    }
  }, [
    selectedSource,
    selectedCategory,
    selectedProvider,
    showPopularOnly,
    searchQuery,
    pagination.limit,
    pagination.offset,
  ]);

  const fetchMetadata = useCallback(async () => {
    try {
      const [categoriesRes, providersRes, statsRes] = await Promise.all([
        mcpApi.getCategories(),
        mcpApi.getProviders(),
        mcpApi.getStats(),
      ]);

      setCategories(categoriesRes.data);
      setProviders(providersRes.data);
      setStats({
        totalTools: statsRes.data.totalTools,
        nativeTools: statsRes.data.nativeTools,
        providerTools: statsRes.data.providerTools,
      });
    } catch (error) {
      console.error('Failed to fetch metadata:', error);
    }
  }, []);

  useEffect(() => {
    fetchMetadata();
  }, [fetchMetadata]);

  useEffect(() => {
    fetchTools();
  }, [fetchTools]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, offset: 0 }));
  }, [selectedSource, selectedCategory, selectedProvider, showPopularOnly, searchQuery]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchTools();
  };

  const handleLoadMore = () => {
    setPagination((prev) => ({
      ...prev,
      offset: prev.offset + prev.limit,
    }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">MCP Tools</h1>
          <p className="text-muted-foreground">
            Browse and invoke {stats?.totalTools.toLocaleString() || '...'} available tools
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => fetchTools()}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          <Button onClick={() => navigate({ to: '/tools/builder' })}>
            <Plus className="mr-2 h-4 w-4" />
            Create Tool
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Native Tools</CardTitle>
            <Wrench className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats?.nativeTools.toLocaleString() || '...'}
            </div>
            <p className="text-xs text-muted-foreground">In-process utilities</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Provider Tools</CardTitle>
            <Database className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats?.providerTools.toLocaleString() || '...'}
            </div>
            <p className="text-xs text-muted-foreground">From connected providers</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Tools</CardTitle>
            <Zap className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats?.totalTools.toLocaleString() || '...'}
            </div>
            <p className="text-xs text-muted-foreground">Available via MCP</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSearch} className="flex flex-col gap-4 md:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search tools..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            <Select value={selectedSource} onValueChange={setSelectedSource}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="native_utility">Native</SelectItem>
                <SelectItem value="provider_endpoint">Provider</SelectItem>
              </SelectContent>
            </Select>

            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.category} value={cat.category}>
                    {cat.category} ({cat.count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedProvider} onValueChange={setSelectedProvider}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Provider" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Providers</SelectItem>
                {providers.slice(0, 50).map((prov) => (
                  <SelectItem key={prov.provider} value={prov.provider}>
                    {prov.displayName} ({prov.count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              type="button"
              variant={showPopularOnly ? 'default' : 'outline'}
              onClick={() => setShowPopularOnly(!showPopularOnly)}
            >
              <Star className="mr-2 h-4 w-4" />
              Popular
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Tools Grid */}
      {loading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <Skeleton className="h-4 w-3/4 mb-2" />
                <Skeleton className="h-3 w-full mb-4" />
                <div className="flex gap-2">
                  <Skeleton className="h-5 w-16" />
                  <Skeleton className="h-5 w-16" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : tools.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Wrench className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">No tools found</h3>
            <p className="text-muted-foreground">Try adjusting your filters or search query</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {tools.map((tool) => (
              <Card
                key={tool.id}
                className="cursor-pointer hover:border-primary transition-colors"
                onClick={() => navigate({ to: `/tools/${encodeURIComponent(tool.id)}` })}
              >
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-medium text-sm truncate flex-1">{tool.name}</h3>
                    <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0 ml-2" />
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2 mb-4 min-h-[2rem]">
                    {tool.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary" className={SOURCE_COLORS[tool.source]}>
                      {SOURCE_LABELS[tool.source]}
                    </Badge>
                    {tool.providerKey && (
                      <Badge variant="outline">{tool.providerKey}</Badge>
                    )}
                    {tool.isPopular && (
                      <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                        <Star className="mr-1 h-3 w-3" />
                        Popular
                      </Badge>
                    )}
                    {tool.isDestructive && (
                      <Badge variant="destructive">
                        <AlertTriangle className="mr-1 h-3 w-3" />
                        Destructive
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Showing {tools.length} of {pagination.total.toLocaleString()} tools
            </p>
            {pagination.hasMore && (
              <Button variant="outline" onClick={handleLoadMore}>
                Load More
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
